<template>
    <Dialog :visible="visible" modal dismissableMask :closable="false" :draggable="false"
        :style="{ width: '60rem', maxWidth: '95vw' }" contentClass="!p-0 overflow-hidden rounded-lg"
        :pt="{ header: { class: '!p-0' } }" @update:visible="(v) => !v && handleClose()">
        <!-- HEADER -->
        <template #header>
            <div
                :class="['flex items-center gap-4 w-full p-3 rounded-t-lg', isDelete ? 'bg-gradient-to-r from-red-50 to-rose-50' : mode === 'create' ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-blue-50 to-indigo-50']">
                <div
                    :class="['w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0', isDelete ? 'bg-red-500' : mode === 'create' ? 'bg-green-500' : 'bg-blue-500']">
                    <i
                        :class="['text-white text-xl', isDelete ? 'fa-regular fa-trash-can' : mode === 'create' ? 'fa-solid fa-plus' : 'fa-regular fa-pen-to-square']" />
                </div>

                <div class="flex-1 min-w-0">
                    <h4 class="font-bold">
                        {{ dialogTitle }}
                    </h4>
                </div>

                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    @click="handleClose()" />
            </div>
        </template>

        <!-- BODY -->
        <form @submit.prevent="onSubmit" class="flex flex-col" style="height: 70vh; max-height: 800px">
            <!-- TABS -->
            <div v-if="!isDelete" class="border-b border-surface-200 mt-2 overflow-x-auto">
                <div class="flex gap-1 min-w-max">
                    <button v-for="tab in tabs" :key="tab.value" type="button" @click="activeTab = tab.value"
                        :class="['px-4 py-3 font-medium relative shrink-0 whitespace-nowrap', activeTab === tab.value ? 'text-primary' : 'hover:text-primary']">
                        <div class="flex items-center gap-2">
                            <i :class="tab.icon" />
                            <span>{{ tab.label }}</span>
                            <Tag v-if="tab.count !== undefined" :value="tab.count" severity="info" />
                        </div>
                        <div v-if="activeTab === tab.value" class="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    </button>
                </div>
            </div>

            <!-- CONTENT -->
            <div class="flex-1 overflow-y-auto p-4">
                <!-- GENERAL TAB -->
                <div v-show="activeTab === 'general' || isDelete" class="space-y-4">
                    <!-- COLOR -->
                    <div v-if="!isDelete">
                        <label class="font-semibold">
                            {{ t('resources.color') }}
                        </label>
                        <div class="flex items-center gap-3 mt-1">
                            <ColorPicker v-model="localResource.color" :disabled="isDelete"
                                :pt="{ preview: { style: 'width: 2.1rem; height: 2.1rem' } }" />
                            <IconField>
                                <InputIcon>#</InputIcon>
                                <InputText v-model="localResource.color" :disabled="isDelete" class="w-28"
                                    placeholder="1b1b7f" />
                            </IconField>
                        </div>
                    </div>

                    <!-- NAME -->
                    <div>
                        <label class="font-semibold"> {{ t('common.name') }} <span class="text-red-500">*</span>
                        </label>
                        <InputText v-model="localResource.name" :disabled="isDelete" required class="w-full mt-1"
                            :placeholder="t('resources.namePlaceholder')" @blur="touched.name = true" />
                        <small v-if="validation.name.value.invalid" class="text-red-500">
                            {{ validation.name.value.message }}
                        </small>
                    </div>

                    <!-- TYPE -->
                    <div>
                        <label class="font-semibold"> {{ t('resources.type') }} <span class="text-red-500">*</span>
                        </label>
                        <Select v-model="localResource.type" :options="resourceTypes" optionLabel="name"
                            :disabled="isDelete" required class="w-full mt-1" :placeholder="t('resources.selectType')"
                            @change="touched.type = true" @blur="touched.type = true" />
                        <small v-if="validation.type.value.invalid" class="text-red-500">
                            {{ validation.type.value.message }}
                        </small>
                    </div>

                    <!-- DESCRIPTION -->
                    <div>
                        <label class="font-semibold">
                            {{ t('common.description') }}
                        </label>
                        <Textarea v-model="localResource.description" rows="4" :disabled="isDelete" class="w-full mt-1"
                            :placeholder="t('resources.descriptionPlaceholder')" />
                    </div>

                    <!-- LOCATION -->
                    <div v-if="!isDelete">
                        <label class="font-semibold">
                            {{ t('resources.location') }}
                        </label>
                        <InputText v-model="localResource.location" :disabled="isDelete" class="w-full mt-1"
                            :placeholder="t('resources.locationPlaceholder')" />
                    </div>

                    <!-- TIMEZONE -->
                    <div v-if="!isDelete">
                        <label class="font-semibold"> {{ t('resources.timezone') }} <span class="text-red-500">*</span>
                        </label>
                        <!--
                            IC-6 / D-07: the option label is the familiar Windows DisplayName first and
                            the IANA id last, composed backend-side in TimezoneDto.Name — which is why
                            optionLabel and filter are untouched here. Two things the label alone cannot
                            do, both via pass-through because PrimeVue 4 teleports this overlay outside
                            the component where a scoped rule cannot reach it:
                              - `option`: let a long row WRAP in the panel. The appended id sits in the
                                tail an ellipsis eats, which is exactly what D-07 needs readable.
                              - `label`: a native tooltip on the CLOSED trigger, which keeps its existing
                                single-line clipping, so the id is reachable without opening the panel.
                            The binding is declared on this element, so it cannot reach any other Select
                            in the application — do not reimplement it as a global stylesheet rule.
                        -->
                        <Select v-model="localResource.timezone" :options="timezones" filter optionLabel="name"
                            :disabled="isDelete" required class="w-full mt-1"
                            :placeholder="t('resources.selectTimezone')" @change="touched.timezone = true"
                            @blur="touched.timezone = true" :pt="{
                                option: { style: 'white-space: normal; word-break: break-word;' },
                                label: { title: localResource.timezone?.name ?? '' }
                            }" />
                        <small v-if="validation.timezone.value.invalid" class="text-red-500">
                            {{ validation.timezone.value.message }}
                        </small>
                        <!--
                            IC-5 / D-09: amber, NOT red, and deliberately not wrapped in the red note's
                            v-else — the field's genuine required-field error and this data-quality
                            warning can both be true at once and must stay distinguishable. The key is
                            read from the calendar.timezone.* namespace on purpose: vue-i18n keys are
                            globally addressable, so this surface reuses the one string rather than
                            duplicating a translation that would then drift.
                        -->
                        <small v-if="isTimezoneUnresolved" class="text-orange-600">
                            {{ t('calendar.timezone.unknown') }}
                        </small>
                    </div>

                    <!-- CAPACITY -->
                    <div v-if="!isDelete" class="flex items-center gap-2 w-full">
                        <Checkbox v-model="capacityEnabled" inputId="capacityEnabled" :binary="true"
                            :disabled="isDelete" />
                        <label for="capacityEnabled" class="font-semibold cursor-pointer">
                            {{ t('resources.enableCapacity') }}
                        </label>
                    </div>
                    <div v-if="!isDelete && capacityEnabled" class="w-full">
                        <label for="capacity" class="font-semibold"> {{ t('resources.capacity') }} <span
                                class="text-red-500">*</span> </label>
                        <InputNumber v-model="localResource.capacity" :min="1" :disabled="isDelete" class="w-full mt-1"
                            :placeholder="t('resources.capacityPlaceholder')" showButtons required
                            @blur="touched.capacity = true" />
                        <small v-if="validation.capacity.value.invalid" class="text-red-500">
                            {{ validation.capacity.value.message }}
                        </small>
                    </div>

                    <!-- STATUS -->
                    <div v-if="!isDelete">
                        <label class="font-semibold"> {{ t('common.status') }} <span class="text-red-500">*</span>
                        </label>
                        <Select v-model="localResource.enabled" :options="[
                            { label: t('common.enabled'), value: true },
                            { label: t('common.disabled'), value: false }
                        ]" optionLabel="label" optionValue="value" :disabled="isDelete" required class="w-full mt-1"
                            @change="touched.enabled = true" @blur="touched.enabled = true" />
                        <small v-if="validation.enabled.value.invalid" class="text-red-500">
                            {{ validation.enabled.value.message }}
                        </small>
                    </div>
                </div>

                <!-- AVAILABILITY TAB -->
                <div v-show="activeTab === 'availability'" class="space-y-4">
                    <ResourceAvailabilitySlots :loading="loadingAvailability" :slots="availabilitySlots"
                        :totalWeeklyHours="totalWeeklyHours" :resourceName="localResource.name"
                        :resourceType="localResource.type?.name || ''" :resourceLocation="localResource.location || ''"
                        :daysOfWeek="daysOfWeek" :getDayLabel="getDayLabel" :getSlotDuration="getSlotDuration"
                        :getDateFromTimestamp="getDateFromTimestamp" :formatDate="formatDate"
                        :slotsPendingDelete="slotsPendingDelete" :checkingDeletable="checkingDeletable"
                        @addSlot="addNewSlot" @removeSlot="removeAvailabilitySlot"
                        @requestRemoveSlot="handleRequestRemoveSlot" @cancelSlotDeletion="cancelSlotDeletion"
                        @updateSlot="updateAvailabilitySlot" @updateSlotStartDate="updateSlotStartDate"
                        @updateSlotEndDate="updateSlotEndDate" @formatSlotTime="formatSlotTime"
                        @applyWeekdays="applyWeekdaysTemplate" @applyFullWeek="applyFullWeekTemplate"
                        @clearAll="clearAllSlots" />
                </div>

                <!-- CUSTOM FIELDS TAB -->
                <div v-show="activeTab === 'customFields'" class="space-y-4">
                    <div class="rounded-lg bg-blue-50 border border-blue-200 p-2">
                        <div class="flex gap-3">
                            <i class="fa-solid fa-info-circle text-blue-500 mt-0.5" />
                            <div>
                                <h6 class="font-semibold text-blue-900 mb-1">{{
                                    t('resources.customFields.managementInfo') }}
                                </h6>
                                <p class="text-blue-800">
                                    {{ t('resources.customFields.managementDescription') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center justify-between">
                        <div>
                            <p>
                                {{ t('resources.customFields.description', { count: selectedCustomFields.length }) }}
                            </p>
                        </div>
                    </div>

                    <!-- SEARCH BAR -->
                    <div>
                        <IconField iconPosition="left">
                            <InputIcon class="fa-solid fa-search" />
                            <InputText :model-value="searchQuery"
                                :placeholder="t('resources.customFields.searchPlaceholder')" class="w-full"
                                @input="onSearchInput(($event.target as HTMLInputElement).value)" />
                        </IconField>
                    </div>

                    <!-- DATA VIEW -->
                    <DataView :value="availableCustomFields" dataKey="id" :loading="loadingCustomFields" paginator
                        :rows="5" :totalRecords="totalRecords" :lazy="true" @page="onPageChange"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        :currentPageReportTemplate="t('resources.customFields.pageReport')">
                        <template #empty>
                            <div class="text-center py-12">
                                <div
                                    class="w-16 h-16 rounded-full bg-surface-100 dark:bg-surface-800 mx-auto mb-4 flex items-center justify-center">
                                    <i class="fa-solid fa-list-check text-2xl" />
                                </div>
                                <h4 class="font-semibold mb-2">{{ t('resources.customFields.noFields') }}</h4>
                                <p>
                                    {{ searchQuery ? t('resources.customFields.noResults') :
                                        t('resources.customFields.noFieldsDescription') }}
                                </p>
                            </div>
                        </template>

                        <template #list="{ items }">
                            <div class="grid grid-cols-1 gap-3">
                                <div v-for="field in items" :key="field.id"
                                    :class="['border-2 rounded-xl p-2 transition-all cursor-pointer', isFieldSelected(field.id) ? 'border-primary bg-primary-50 dark:bg-primary-900/20' : 'border-surface-200 hover:border-primary hover:bg-surface-50']"
                                    @click="toggleCustomField(field)">
                                    <div class="flex justify-between items-center gap-4">
                                        <div class="flex items-center gap-3 flex-1 min-w-0">
                                            <div>
                                                <i
                                                    :class="['text-xl', isFieldSelected(field.id) ? 'fa-solid fa-check-circle text-primary' : 'fa-regular fa-circle']" />
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="font-bold mb-0 truncate">{{ field.title }}</p>
                                                <p v-if="field.description" class="truncate">
                                                    {{ field.description }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="flex-shrink-0">
                                            <Button v-if="!isFieldSelected(field.id)" icon="fa-solid fa-plus"
                                                :label="t('resources.customFields.select')" outlined
                                                @click.stop="toggleCustomField(field)" />
                                            <Button v-else icon="fa-solid fa-check"
                                                :label="t('resources.customFields.selected')" severity="success"
                                                @click.stop="toggleCustomField(field)" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </DataView>
                </div>
            </div>

            <!-- FOOTER -->
            <div class="flex justify-end p-3 border-t bg-gray-50 mt-auto gap-2">
                <Button v-if="isDelete" type="submit" :label="t('common.delete')" severity="danger"
                    :loading="loading" />
                <Button v-else type="submit" :label="mode === 'create' ? t('common.create') : t('common.save')"
                    severity="success" :loading="loading" :disabled="!isFormValid" />
            </div>
        </form>
    </Dialog>

    <!-- Delete Slot Confirmation Dialog -->
    <Dialog v-model:visible="deleteConfirmVisible" modal :closable="true" :draggable="false"
        :style="{ width: '30rem', maxWidth: '95vw' }" contentClass="!p-0 overflow-hidden rounded-lg"
        :pt="{ header: { class: '!p-0' } }">
        <template #header>
            <div class="flex items-center gap-3 w-full p-3 bg-gray-50 rounded-t-lg">
                <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-triangle-exclamation text-amber-500 text-lg"></i>
                </div>
                <h4 class="text-xl font-semibold">{{ t('availability.deleteSlotConfirm.title') }}</h4>
            </div>
        </template>
        <div class="p-3 space-y-4">
            <div class="flex gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                <i class="fa-solid fa-triangle-exclamation text-amber-500 text-xl mt-0.5"></i>
                <div>
                    <h5 class="font-semibold text-amber-900 mb-1">{{ t('availability.deleteSlotConfirm.warning') }}</h5>
                    <p class="text-sm text-amber-800">{{ t('availability.deleteSlotConfirm.hasReservations') }}</p>
                </div>
            </div>

            <div v-if="deleteConfirmSlot" class="p-3 rounded-lg bg-surface-100 border border-surface-200">
                <p class="font-medium text-sm">
                    {{ deleteConfirmSlot.slot.title || getDayLabel(deleteConfirmSlot.slot.dayOfWeek) }}
                </p>
                <p class="text-sm">{{ getDayLabel(deleteConfirmSlot.slot.dayOfWeek) }} • {{ deleteConfirmSlot.slot.start
                }}
                    - {{ deleteConfirmSlot.slot.end }}</p>
            </div>

            <div>
                <label class="block font-semibold text-sm mb-2"> {{ t('availability.deleteSlotConfirm.reasonLabel') }}
                    <span class="text-red-500">*</span> </label>
                <Textarea v-model="deleteReason" rows="3" class="w-full"
                    :placeholder="t('availability.deleteSlotConfirm.reasonPlaceholder')" />
                <p class="mt-1">{{ t('availability.deleteSlotConfirm.reasonHint') }}</p>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end p-3 border-t bg-gray-50">
                <Button :label="t('availability.deleteSlotConfirm.proceed')" severity="danger"
                    :disabled="!deleteReason.trim()" @click="confirmSlotDeletion" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import ResourceAvailabilitySlots from '@/components/resources/ResourceAvailabilitySlots.vue';
import { useResourceAvailability } from '@/composables/useResourceAvailability';
import { AvailabilitySlot } from '@/lib/types/availability';
import { CustomField } from '@/lib/types/customField';
import { Resource } from '@/lib/types/resource';
import { getErrorMessage } from '@/plugins/i18n/enums';
import { useCustomFieldsStore } from '@/store/customField';
import { useResourcesStore } from '@/store/resource';
import { useToast } from 'primevue/usetoast';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const toast = useToast();
const store = useResourcesStore();
const customFieldsStore = useCustomFieldsStore();

// Availability composable
const {
    slots: availabilitySlots,
    loading: loadingAvailability,
    totalWeeklyHours,
    daysOfWeek,
    getDayLabel,
    getSlotDuration,
    getDateFromTimestamp,
    updateSlotStartDate,
    updateSlotEndDate,
    formatDate,
    formatSlotTime,
    loadAvailability,
    addNewSlot,
    removeSlot: removeAvailabilitySlot,
    checkSlotDeletable,
    markSlotForDeletion,
    cancelSlotDeletion,
    slotsPendingDelete,
    checkingDeletable,
    clearPendingDeletions,
    applyWeekdaysTemplate,
    applyFullWeekTemplate,
    clearAllSlots,
    saveAvailability
} = useResourceAvailability();

const props = defineProps<{
    visible: boolean;
    mode: 'create' | 'edit' | 'delete';
    resource: Resource | null;
}>();

const emit = defineEmits(['close', 'saved']);

/* --------------------
   STATE
-------------------- */

const activeTab = ref('general');
const loading = ref(false);
const selectedCustomFields = ref<CustomField[]>([]);
const searchQuery = ref('');
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const currentPage = ref(0);
const totalRecords = ref(0);
const capacityEnabled = ref(false);
const originalResourceColor = ref<string | null>(null);

// Track which fields have been touched for validation
const touched = reactive({
    name: false,
    type: false,
    timezone: false,
    capacity: false,
    enabled: false
});

// Field validation computed properties
const validation = {
    name: computed(() => ({
        invalid: touched.name && (!localResource.value.name || localResource.value.name.trim() === ''),
        message: t('resources.validation.nameRequired')
    })),
    type: computed(() => ({
        invalid: touched.type && !localResource.value.type,
        message: t('resources.validation.typeRequired')
    })),
    timezone: computed(() => ({
        // D-09: a degraded resource — one whose stored timezone_id resolved to nothing — has a
        // POPULATED column, so the required-field error must not fire on it. See isFormValid.
        invalid: touched.timezone && !localResource.value.timezone && !localResource.value.timezoneId,
        message: t('resources.validation.timezoneRequired')
    })),
    capacity: computed(() => ({
        invalid: touched.capacity && capacityEnabled.value && (!localResource.value.capacity || localResource.value.capacity <= 0),
        message: t('resources.validation.capacityRequired')
    })),
    enabled: computed(() => ({
        invalid: touched.enabled && localResource.value.enabled === undefined,
        message: t('resources.validation.statusRequired')
    }))
};

// Touch all fields for validation on submit
function touchAllFields() {
    Object.keys(touched).forEach((key) => {
        (touched as any)[key] = true;
    });
}

// Reset touched state
function resetTouched() {
    Object.keys(touched).forEach((key) => {
        (touched as any)[key] = false;
    });
}

// Check if form is valid
const isFormValid = computed(() => {
    // Skip validation for delete mode
    if (isDelete.value) return true;

    const nameValid = localResource.value.name && localResource.value.name.trim() !== '';
    const typeValid = !!localResource.value.type;
    // D-09: `timezoneId` is the raw stored id and `timezone` the entry it resolved to. Requiring the
    // resolved object alone made resourceToDTO's deliberate `?? resource.timezoneId` floor
    // unreachable from the UI: a DEGRADED resource is exactly one whose `timezone` is null, so an
    // operator could not change its name, colour or capacity without first re-picking a timezone —
    // which overwrites the stored id and destroys the evidence of the misconfiguration. Between
    // migration steps 2 and 3 that is every resource. A populated raw id therefore satisfies the
    // requirement; only a genuinely empty column blocks the save.
    const timezoneValid = !!localResource.value.timezone || !!localResource.value.timezoneId;
    const capacityValid = !capacityEnabled.value || (localResource.value.capacity && localResource.value.capacity > 0);
    const enabledValid = localResource.value.enabled !== undefined;

    return nameValid && typeValid && timezoneValid && capacityValid && enabledValid;
});

// Delete confirmation dialog state
const deleteConfirmVisible = ref(false);
const deleteConfirmSlot = ref<{ slot: AvailabilitySlot; index: number } | null>(null);
const deleteReason = ref('');
const deleteHasReservations = ref(false);

const resourceTypes = computed(() => store.types);
const timezones = computed(() => store.timezones);

function emptyResource(): Resource {
    return {
        id: '',
        type: resourceTypes.value?.[0] ?? null,
        name: '',
        description: '',
        location: '',
        timezone: timezones.value?.[0] ?? null,
        // Resource.timezoneId is the raw stored id (D-09). A brand-new resource has none yet, so it
        // seeds from the pre-selected picker entry; resourceToDTO prefers the resolved object anyway.
        timezoneId: timezones.value?.[0]?.id ?? '',
        capacity: 0,
        color: '1b1b7f',
        customFields: [],
        enabled: true
    };
}

const localResource = ref<Resource>(emptyResource());

// UI-SPEC E4 / D-09. The gate is "a STORED id resolved to nothing": the served list has LOADED and
// the id still matched nothing in it. Never "the list is empty" — `timezones` is [] both before the
// fetch resolves and after it fails, and a resource must not be accused of bad configuration merely
// because a request is in flight.
//
// `props.resource !== null` is the create-mode term. emptyResource() seeds `timezone` from
// `timezones.value?.[0] ?? null` and the `visible` watcher assigns it once at open, so opening the
// modal in create mode before /timezones resolves leaves `localResource.timezone` null permanently;
// when the fetch lands, `timezonesLoaded` flips true and the amber note accused the operator of bad
// data about a record that does not exist yet. This does NOT diverge from ResourceSlotCard's gate:
// that card only ever receives a PERSISTED Resource, so it has no create-mode state to disagree on.
// Adding the term makes the two gates agree on the only state both can observe.
const isTimezoneUnresolved = computed(() => store.timezonesLoaded && props.resource !== null && localResource.value.timezone === null);


/* --------------------
   COMPUTED
-------------------- */

const isDelete = computed(() => props.mode === 'delete');

const dialogTitle = computed(() => (props.mode === 'create' ? t('resources.newResource') : props.mode === 'edit' ? t('resources.editResource') : t('resources.deleteResource')));

const tabs = computed(() => [
    {
        value: 'general',
        label: t('resources.tabs.general'),
        icon: 'fa-solid fa-gear'
    },
    {
        value: 'customFields',
        label: t('resources.tabs.customFields'),
        icon: 'fa-solid fa-list-check',
        count: selectedCustomFields.value.length
    },
    {
        value: 'availability',
        label: t('resources.tabs.availability'),
        icon: 'fa-regular fa-calendar',
        count: availabilitySlots.value.length
    }
]);

const loadingCustomFields = computed(() => customFieldsStore.loading);

const allCustomFields = computed(() => customFieldsStore.customFields);

const availableCustomFields = computed(() => {
    // Return all fields from store (already filtered and sorted by API)
    return allCustomFields.value.filter((field) => field && field.enabled);
});

/* --------------------
   CUSTOM FIELDS
-------------------- */



function isFieldSelected(fieldId: string): boolean {
    return selectedCustomFields.value.some((sf) => sf.id === fieldId);
}

async function loadCustomFields(page: number = 0) {
    try {
        const filters: Record<string, any> = {
            enabled: true
        };

        // Add title filter if search query exists
        if (searchQuery.value.trim()) {
            filters.title = { value: searchQuery.value.trim() };
        }

        const metadata = await customFieldsStore.fetchPagedData(
            page + 1, // API pages are 1-indexed
            5, // Page size
            'title', // Sort by title
            1, // Sort order ASC
            filters
        );

        if (metadata) {
            totalRecords.value = metadata.total;
        }
    } catch (err) {
        console.error('Failed to load custom fields:', err);
    }
}

function onPageChange(event: any) {
    currentPage.value = event.page;
    loadCustomFields(event.page);
}

function onSearchInput(value: string) {
    searchQuery.value = value;
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }
    searchDebounceTimer = setTimeout(() => {
        currentPage.value = 0;
        loadCustomFields(0);
    }, 500);
}

function toggleCustomField(field: CustomField) {
    const index = selectedCustomFields.value.findIndex((sf) => sf.id === field.id);

    if (index >= 0) {
        // Remove if already selected
        selectedCustomFields.value.splice(index, 1);
    } else {
        // Add if not selected
        selectedCustomFields.value.push(field);
    }

    localResource.value.customFields = [...selectedCustomFields.value];
}

function removeCustomField(fieldId: string) {
    selectedCustomFields.value = selectedCustomFields.value.filter((cf) => cf.id !== fieldId);
    localResource.value.customFields = [...selectedCustomFields.value];
}

/* --------------------
   AVAILABILITY
-------------------- */

function updateAvailabilitySlot(index: number, field: string, value: any) {
    if (availabilitySlots.value[index]) {
        (availabilitySlots.value[index] as any)[field] = value;
    }
}

/**
 * Handle request to remove a slot - checks if deletable first
 */
async function handleRequestRemoveSlot(slot: AvailabilitySlot, index: number) {
    // New slots (no id) can be removed immediately
    if (!slot.id) {
        removeAvailabilitySlot(index);
        return;
    }

    // Check if slot can be deleted (no reservations)
    const isDeletable = await checkSlotDeletable(slot);

    if (isDeletable) {
        // No reservations - mark for deletion directly
        markSlotForDeletion(slot, index, false);
    } else {
        // Has reservations - show confirmation dialog
        deleteConfirmSlot.value = { slot, index };
        deleteHasReservations.value = true;
        deleteReason.value = '';
        deleteConfirmVisible.value = true;
    }
}

/**
 * Confirm deletion of a slot with reservations
 */
function confirmSlotDeletion() {
    if (!deleteConfirmSlot.value) return;

    const { slot, index } = deleteConfirmSlot.value;
    markSlotForDeletion(slot, index, true, deleteReason.value || undefined);

    // Reset dialog state
    deleteConfirmVisible.value = false;
    deleteConfirmSlot.value = null;
    deleteReason.value = '';
    deleteHasReservations.value = false;
}

/**
 * Cancel the deletion dialog
 */
function cancelDeleteDialog() {
    deleteConfirmVisible.value = false;
    deleteConfirmSlot.value = null;
    deleteReason.value = '';
    deleteHasReservations.value = false;
}

/* --------------------
   WATCHERS
-------------------- */

watch(
    () => [props.visible, props.resource] as [boolean, Resource | null],
    async ([visible, resource]) => {
        if (!visible) return;

        activeTab.value = 'general';
        searchQuery.value = '';
        currentPage.value = 0;

        // Reset touched state
        resetTouched();

        // Clear pending deletions when dialog opens
        clearPendingDeletions();

        // Load first page of custom fields
        await loadCustomFields(0);

        if (resource) {
            const colorWithoutHash = resource.color?.replace('#', '') || '1b1b7f';
            originalResourceColor.value = colorWithoutHash;
            localResource.value = {
                ...resource,
                color: colorWithoutHash,
                customFields: resource.customFields ? [...resource.customFields] : []
            };
            selectedCustomFields.value = resource.customFields ? [...resource.customFields] : [];
            capacityEnabled.value = resource.capacity !== null && resource.capacity !== undefined && resource.capacity > 0;

            // Load availability slots
            await loadAvailability(resource.id);
        } else {
            originalResourceColor.value = null;
            localResource.value = emptyResource();
            selectedCustomFields.value = [];
            capacityEnabled.value = false;
            availabilitySlots.value = [];
        }
    },
    { immediate: true }
);

/* --------------------
   SUBMIT
-------------------- */

async function onSubmit() {
    // Touch all fields to show validation errors
    touchAllFields();

    // Check if form is valid (skip for delete mode)
    if (!isDelete.value && !isFormValid.value) {
        return;
    }

    loading.value = true;
    try {
        // Ensure custom fields are synced
        localResource.value.customFields = [...selectedCustomFields.value];

        // Handle capacity based on checkbox
        if (!capacityEnabled.value) {
            localResource.value.capacity = 0;
        }

        if (props.mode === 'create') {
            const res = await store.createOne(localResource.value);

            if (res && 'errorCode' in res) {
                toast.add({
                    severity: 'error',
                    summary: t('error.title'),
                    detail: getErrorMessage(res, t),
                    life: 5000
                });
                return;
            }

            // Save availability for new resource
            if (availabilitySlots.value.length > 0) {
                await saveAvailability(res.id);
            }
        } else if (props.mode === 'edit') {
            if (!localResource.value.id) {
                throw new Error('Resource id missing during update');
            }
            const error = await store.updateOne(localResource.value);

            if (error) {
                toast.add({
                    severity: 'error',
                    summary: t('error.title'),
                    detail: getErrorMessage(error, t),
                    life: 5000
                });
                return;
            }

            // Save availability for existing resource
            await saveAvailability(localResource.value.id);
        } else if (props.mode === 'delete') {
            if (!localResource.value.id) {
                throw new Error('Resource id missing during delete');
            }
            const error = await store.deleteOne(localResource.value.id);
            if (error) {
                toast.add({
                    severity: 'error',
                    summary: t('error.title'),
                    detail: getErrorMessage(error, t),
                    life: 5000
                });
            }
        }

        emit('saved');
        handleClose();
    } catch (err) {
        console.error('Resource save failed:', err);
        toast.add({
            severity: 'error',
            summary: t('error.title'),
            detail: err instanceof Error ? err.message : String(err),
            life: 5000
        });
    } finally {
        loading.value = false;
    }
}

function handleClose() {
    emit('close');
}
</script>
